/**
 * Builder for the Clinic structured list-query contract (verified):
 * one URL-encoded JSON object in ?query= — filters / sort / limit and
 * EITHER page (numbered mode, ZOY-285) OR cursor (keyset mode); both at once
 * is 400 QUERY_INVALID. Ops: eq neq contains in gte lte between isnull ·
 * limit default 20, max 100 · page 1..100000.
 */

export const FILTER_OPS = [
  'eq',
  'neq',
  'contains',
  'in',
  'gte',
  'lte',
  'between',
  'isnull',
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export interface QueryFilter {
  field: string;
  op: FilterOp;
  value?: unknown;
}

export interface QuerySort {
  field: string;
  dir: 'asc' | 'desc';
}

export interface QuerySpecInput {
  filters?: readonly QueryFilter[];
  sort?: readonly QuerySort[];
  limit?: number;
  /** Numbered mode: 1-based page fetched directly (the pager's mode). */
  page?: number;
  /** Keyset mode: continuation from a previous nextCursor (programmatic walks). */
  cursor?: string;
}

export const QUERY_DEFAULT_LIMIT = 20;
export const QUERY_MAX_LIMIT = 100;
export const QUERY_MAX_PAGE = 100_000;

/** Serializes a spec for the ?query= parameter; omits empty members. */
export function buildQueryParam(spec: QuerySpecInput): string | undefined {
  const out: Record<string, unknown> = {};
  if (spec.filters && spec.filters.length > 0) out.filters = spec.filters;
  if (spec.sort && spec.sort.length > 0) out.sort = spec.sort;
  if (spec.limit !== undefined) {
    out.limit = Math.min(Math.max(1, Math.floor(spec.limit)), QUERY_MAX_LIMIT);
  }
  // page XOR cursor — a page request never carries a cursor (400 QUERY_INVALID).
  if (spec.page !== undefined) {
    out.page = Math.min(Math.max(1, Math.floor(spec.page)), QUERY_MAX_PAGE);
  } else if (spec.cursor !== undefined && spec.cursor !== '') {
    out.cursor = spec.cursor;
  }
  if (Object.keys(out).length === 0) return undefined;
  return JSON.stringify(out);
}

/**
 * The ZOY-285 envelope every paginated Clinic list returns (verified,
 * docs/api/pagination.md). `total` counts the SAME filtered, scoped set as
 * `items`. Numbered mode fills page/range/hasPrevious; keyset mode leaves
 * them null. `nextCursor` is present in both modes.
 */
export interface PagedResult<T> {
  items: readonly T[];
  /** == pageSize (kept for existing consumers). */
  limit: number;
  pageSize: number;
  total: number;
  /** max(1, ceil(total / pageSize)). */
  totalPages: number;
  /** Numbered mode: the requested page · keyset mode: null. */
  page: number | null;
  /** 1-based inclusive · null when the page is empty. */
  rangeStart: number | null;
  rangeEnd: number | null;
  /** Numbered mode only · null in keyset mode. */
  hasPrevious: boolean | null;
  hasNext: boolean;
  /** Keyset continuation after a FULL page, else null — in both modes. */
  nextCursor: string | null;
}
